import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

interface DrawerSearchProps {
  search: string;
  onChange: (value: string) => void;
  onClear: () => void;
  /** Placeholder — defaults to the column-search label. */
  placeholder?: string;
}

export function DrawerSearch({ search, onChange, onClear, placeholder }: DrawerSearchProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const resolvedPlaceholder = placeholder ?? t('columnConfigurator.searchPlaceholder');

  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        borderBottom: '1px solid var(--gray-100)',
        flexShrink: 0,
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder={resolvedPlaceholder}
        value={search}
        onChange={e => onChange(e.target.value)}
        autoFocus
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{ color: search ? primary : 'var(--gray-400)', transition: 'color 0.2s ease' }}
                />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={onClear}
                  sx={{
                    color: 'var(--gray-400)',
                    p: 0.5,
                    '&:hover': { color: 'var(--gray-700)', bgcolor: 'var(--gray-100)' },
                  }}
                >
                  <ClearIcon sx={{ fontSize: 'var(--icon-action)' }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
            sx: {
              borderRadius: '10px',
              bgcolor: search ? 'white' : 'var(--gray-50)',
              fontSize: 'var(--font-counter-value)',
              transition: 'background-color 0.2s ease',
              color: 'var(--gray-900)',
            },
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.2s ease',
            '& fieldset': { borderColor: 'var(--gray-200)', borderWidth: 1.5, transition: 'all 0.2s ease' },
            '&:hover fieldset': { borderColor: 'var(--gray-300)' },
            '&.Mui-focused fieldset': { borderColor: primary, borderWidth: 2 },
            ...(search && { '& fieldset': { borderColor: primary, borderWidth: 2 } }),
          },
        }}
      />
    </Box>
  );
}
